import nuclides, {
  getDecayChain,
  getNuclide,
} from "core/services/nuclideService";
import { getContrast } from "core/utils/utils";
import InteractiveCanvas from "./InteractiveCanvas";
import { BLACK, BLUE, GRAY, MAGENTA, RED, WHITE } from "./theme/palette";

const DRAW_TEXT_BP = 20;
const DRAW_FULL_TEXT_BP = 80;
class NuclideCanvas extends InteractiveCanvas {
  constructor(props) {
    super({
      ...props,
      onSelect: (...args) => {
        this.currentDecayChain = null;
        return props.onSelect(...args);
      },
      maxBlockX: nuclides.highest.neutron,
      maxBlockY: nuclides.highest.proton,
    });

    this.TAG = "NUCLIDE_GRAPH";

    this.currentDecayChain = null;
    this.shouldDrawCoord = true;
    this.shouldDrawDecayChain = props.shouldDrawDecayChain;
    this.shouldDrawSecondaryChain = props.shouldDrawSecondaryChain;
  }

  reTarget(x, y) {
    const p = Math.floor(this.viewCoord.y);
    const n = Math.floor(this.viewCoord.x);

    const my = this.canvasHeight / this.bp / 2;
    const mx = this.canvasWidth / this.bp / 2;
    let pMin = Math.floor(p - my);
    let nMin = Math.floor(n - mx);
    let pMax = Math.floor(p + my);
    let nMax = Math.floor(n + mx);

    if (y < pMin) {
      this.viewCoord.y -= pMin - y + 1;
    }
    if (y > pMax) {
      this.viewCoord.y += y - pMax;
    }
    if (x < nMin) {
      this.viewCoord.x -= nMin - x + 1;
    }
    if (x > nMax) {
      this.viewCoord.x += x - nMax;
    }
  }

  drawNuclideArray(startNuclide, endNuclide, startXY, endXY) {
    const hbp = this.bp / 2;
    const hhbp = this.bp >= this.cp(DRAW_TEXT_BP) ? hbp / 2 : hbp;

    const dx =
      endNuclide.neutron > startNuclide.neutron ? this.bp - hhbp : hhbp;
    const dy = endNuclide.proton > startNuclide.proton ? hhbp : this.bp - hhbp;
    this.ctx.globalAlpha = 0.6;
    this.drawArrow(
      startXY.x + dx,
      startXY.y + dy,
      endXY.x + this.bp - dx,
      endXY.y + this.bp - dy
    );
  }

  drawDecayChain(decayChain, crossed) {
    if (decayChain && decayChain.hasDaughter()) {
      this.ctx.strokeStyle = BLUE;
      this.ctx.globalAlpha = 1;
      let { daughters, nuclide: startNuclide } = decayChain;
      const startXY = this.getXYFromNP(
        startNuclide.neutron,
        startNuclide.proton
      );

      let endNuclide = null;

      for (const daughter of daughters) {
        if (this.shouldDrawSecondaryChain || !daughter.isSecondary) {
          endNuclide = daughter.nuclide;

          const endXY = this.getXYFromNP(endNuclide.neutron, endNuclide.proton);

          if (
            !daughter.isCircular &&
            !daughter.hasDaughter() &&
            !crossed.includes(daughter.nuclide)
          ) {
            crossed.push(daughter.nuclide);
            this.ctx.strokeRect(endXY.x, endXY.y, this.bp, this.bp);
            if (this.bp < this.cp(DRAW_FULL_TEXT_BP)) {
              this.ctx.globalAlpha = 0.3;
              this.drawCross(endXY.x, endXY.y);
            }
          } else if (this.bp > this.cp(DRAW_TEXT_BP)) {
            this.ctx.strokeStyle = MAGENTA;
            this.ctx.globalAlpha = 0.5;
            this.ctx.strokeRect(endXY.x, endXY.y, this.bp, this.bp);
          }
          this.ctx.strokeStyle = BLUE;
          this.ctx.globalAlpha = 1;
          this.drawDecayChain(daughter, crossed);
          this.drawNuclideArray(startNuclide, endNuclide, startXY, endXY);
        }
      }
    }
  }

  drawNuclide(nu) {
    const { x, y } = this.getXYFromNP(nu.neutron, nu.proton);

    const hbp = this.bp / 2;
    const nuColor = nu.getColor();

    this.ctx.fillStyle = nuColor;

    this.ctx.fillRect(x, y, this.bp, this.bp);

    //if the block pixel is greater than 20, display text
    if (this.bp >= this.cp(DRAW_TEXT_BP)) {
      const contrast = getContrast(nuColor, true);
      this.ctx.fillStyle = contrast;
      this.ctx.lineWidth = this.cp(0.3);
      this.ctx.strokeStyle = GRAY;
      this.ctx.strokeRect(x, y, this.bp, this.bp);

      this.drawText(nu.symbol, x + hbp, y + hbp, `bold ${this.bp / 5}px`);

      if (this.bp >= this.cp(DRAW_FULL_TEXT_BP)) {
        this.drawText(
          nu.name,
          x + hbp,
          y + this.bp * 0.8,
          `bold ${this.bp / 8}px`
        );
      }
    }
  }

  coords = [
    //top left
    {
      showSymbol: true,
      x: () => this.cp(2),
      y: () => 0,
      offsetX: () => this.cp(-1),
      offsetY: () => this.cp(-1),
      height: this.cp(12),
      align: "left",
    },
    //bottom left
    {
      x: () => this.cp(2),
      y: () => this.canvasHeight,
      offsetX: () => this.cp(-1),
      offsetY: () => this.cp(-12),
      height: this.cp(12),
      align: "left",
    },
    //top right
    {
      x: () => this.canvasWidth,
      y: () => 0,
      offsetX: (tw) => -(tw + this.cp(4)),
      offsetY: () => this.cp(-1),
      height: this.cp(12),
      align: "right",
    },
    //bottom right
    {
      x: () => this.canvasWidth,
      y: () => this.canvasHeight,
      offsetX: (tw) => -(tw + this.cp(4)),
      offsetY: () => this.cp(-12),
      height: this.cp(12),
      align: "right",
    },
  ];

  drawCoord() {
    this.ctx.globalAlpha = 0.7;
    this.ctx.font = `bolder ${this.cp(10)}px Arial`;

    for (let coord of this.coords) {
      this.ctx.fillStyle = WHITE;
      const x = coord.x();
      const y = coord.y();
      const { p, n } = this.getNPFromXY(x, y);
      let text = `Z:${p} N:${n}`;
      if (coord.showSymbol && this.selectedTarget?.symbol) {
        text += ` | ${this.selectedTarget?.symbol}`;
      }
      const textWidth = this.ctx.measureText(text).width;
      const offsetX = coord.offsetX(textWidth);
      const offsetY = coord.offsetY();
      const buffer = this.cp(2);

      this.roundRect(
        x + offsetX,
        y + offsetY,
        textWidth + buffer * 2,
        coord.height,
        5,
        true,
        false
      );
      this.ctx.fillStyle = BLACK;
      this.ctx.textAlign = coord.align;
      this.ctx.fillText(
        text,
        coord.align === "right" ? x - buffer : x - offsetX,
        y + coord.height - buffer + offsetY
      );
    }
  }

  draw() {
    //clear canvas
    this.ctx.globalAlpha = 1;
    this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

    this.ctx.fillStyle = WHITE;
    this.ctx.textAlign = "center";
    this.ctx.lineWidth = this.cp(0.5);

    const p = Math.floor(this.viewCoord.y);
    const n = Math.floor(this.viewCoord.x);

    const my = this.canvasHeight / this.bp / 2;
    const mx = this.canvasWidth / this.bp / 2;
    let pMin = Math.floor(p - my - this.renderExtra);
    let nMin = Math.floor(n - mx - this.renderExtra);
    let pMax = Math.floor(p + my + this.renderExtra);
    let nMax = Math.floor(n + mx + this.renderExtra);

    const pEnd = Math.min(nuclides.pMax, pMax);
    let pStart = Math.max(pMin, nuclides.pMin);

    for (; pStart <= pEnd; pStart++) {
      const entry = nuclides[pStart];
      if (entry) {
        let nStart = Math.max(nMin, entry.nMin);
        const nEnd = Math.min(entry.nMax, nMax);
        for (; nStart <= nEnd; nStart++) {
          const target = entry[nStart];

          if (target) {
            this.drawNuclide(target);
          }
        }
      }
    }

    //if there is a target, highlight target
    if (this.selectedTarget) {
      const { x, y } = this.getXYFromNP(
        this.selectedTarget.neutron,
        this.selectedTarget.proton
      );
      this.ctx.lineWidth = this.cp(2);
      this.ctx.strokeStyle = RED;

      this.ctx.strokeRect(x, y, this.bp, this.bp);

      const nuclide = getNuclide(this.selectedTarget);
      if (nuclide) {
        this.ctx.lineWidth = this.cp(this.bp >= this.cp(DRAW_TEXT_BP) ? 2 : 1);

        if (this.bp < this.cp(DRAW_FULL_TEXT_BP)) {
          this.ctx.globalAlpha = 0.3;
          this.drawCross(x, y);
        } else {
          this.ctx.lineWidth = this.cp(3);
        }

        if (this.shouldDrawDecayChain) {
          this.drawDecayChain(
            (this.currentDecayChain =
              this.currentDecayChain || getDecayChain(nuclide)),
            []
          );
        }
      }
    }

    if (this.shouldDrawCoord) {
      this.drawCoord();
    }
  }
}

export default NuclideCanvas;
